import { useEffect, useRef } from "react"
import { useDispatch, useSelector } from "react-redux"
import { Creators as NotificationCreators } from "modules/notifications/model"
import { LOADING } from "helpers/data-statuses"
import { getIsIntradayAnalyticsEnabled } from "selectors/features"
import { useIntradayState } from "./use-intraday-state"
import { useAppSelector } from "hooks/redux"
import { useDashboardId } from "../context/dashboard-id"
import { DEFAULT_CORE_STATE } from "../store/core-slice"

/**
 * Custom hook to handle intraday warning notifications
 *
 * Intraday mode is defined as:
 * 1. Intraday feature flag enabled
 * 2. Current main dashboard instrument is an intraday-enabled portfolio
 * 3. Intraday data has been uploaded for that portfolio
 * 4. End date is today
 *
 * Shows an intraday data warning toast when 1, 2 are true but 3 is false (4 is false inherently)
 */
export function useIntradayWarningNotification() {
  const dispatch = useDispatch()
  const intradayDataWarningShownRef = useRef(false)
  const dashboardId = useDashboardId()
  const { isIntraday, instrumentsMetadata } = useAppSelector((s) => s.dashboard.core[dashboardId] ?? DEFAULT_CORE_STATE)
  const [{ intradayDataAvailable }, intradayBatchStatus] = useIntradayState()
  const isIntradayAnalyticsEnabled = useSelector(getIsIntradayAnalyticsEnabled)
  const isIntradayInstrument = !!(instrumentsMetadata?.original as { intradayEnabled?: boolean })?.intradayEnabled

  useEffect(() => {
    if (isIntradayAnalyticsEnabled) {
      intradayDataWarningShownRef.current = false
      return
    }
    // Only show warning for actual intraday instruments that don't have data yet
    if (isIntradayInstrument && isIntraday && !intradayDataAvailable && intradayBatchStatus !== LOADING) {
      if (!intradayDataWarningShownRef.current) {
        intradayDataWarningShownRef.current = true
        dispatch(NotificationCreators.add("warning", null, "No intraday data. Using end of previous day."))
      }
    } else if (!isIntradayInstrument || !isIntraday || intradayDataAvailable) {
      // Reset the warning flag when:
      // - Current instrument is not intraday-enabled, OR
      // - Intraday is not enabled, OR
      // - Intraday batch becomes available
      intradayDataWarningShownRef.current = false
    }
  }, [isIntradayAnalyticsEnabled, isIntradayInstrument, isIntraday, intradayDataAvailable, intradayBatchStatus, dispatch])
}
